// Math rendering utility using KaTeX
import { renderMathInElement } from './katexAutoRender';

declare global {
  interface Window {
    katex: any;
  }
}

/**
 * Detects if text contains LaTeX math expressions (even without delimiters)
 */
function containsMathExpressions(text: string): boolean {
  // Common LaTeX commands that indicate math content
  const mathPatterns = [
    /\\frac\{[^}]*\}\{[^}]*\}/g, // \frac{}{}
    /\\sqrt\{[^}]*\}/g, // \sqrt{}
    /\\sum/g, // \sum
    /\\int/g, // \int
    /\\lim/g, // \lim
    /\\alpha|\\beta|\\gamma|\\delta|\\epsilon|\\theta|\\lambda|\\mu|\\pi|\\sigma|\\phi|\\psi|\\omega/g, // Greek letters
    /(?:^|[^a-zA-Z])([a-zA-Z])\s*=\s*(?:[0-9+\-*/^()]|\b[a-zA-Z](?:[0-9+\-*/^()]|\b))/g, // Simple equations like y=2x+3 or x=y2, but not word=word
    /\^[a-zA-Z0-9{},]/g, // Superscripts
    /_[a-zA-Z0-9{},]/g, // Subscripts
  ];

  return mathPatterns.some((pattern) => pattern.test(text));
}

/**
 * Wraps potential math expressions with inline delimiters if they aren't already wrapped
 */
function preprocessMathContent(content: string): string {
  // First, protect already delimited math
  const protectedSections: string[] = [];
  let protectedContent = content;

  // Protect existing delimited sections
  const existingDelimiters = [
    /\$\$[\s\S]*?\$\$/g,
    /\\\([\s\S]*?\\\)/g,
    /\\\[[\s\S]*?\\\]/g,
  ];

  existingDelimiters.forEach((delimiter) => {
    protectedContent = protectedContent.replace(delimiter, (match) => {
      const index = protectedSections.length;
      protectedSections.push(match);
      return `__PROTECTED_MATH_${index}__`;
    });
  });

  // Now process undelimited math expressions
  // Handle fractions
  protectedContent = protectedContent.replace(
    /\\frac\{([^}]*)\}\{([^}]*)\}/g,
    '\\(\\frac{$1}{$2}\\)',
  );

  // Handle simple equations (avoid wrapping already wrapped content)
  // Look for equations that aren't already inside math delimiters
  protectedContent = protectedContent.replace(
    /(?:^|\s)([a-zA-Z])\s*=\s*(?:[0-9+\-*/^()]|\b[a-zA-Z](?:[0-9+\-*/^()]|\b))/g,
    (match, equation) => {
      // If it's a common word before equals, just style the equals sign
      if (match.match(/\b(?:or|and|to|the|in|on|at|by|for)\s*=/i)) {
        return match.replace(/\s*(=)\s*/, ' \\($1\\) ');
      }
      return `\\(${equation}\\)`;
    },
  );

  // Style standalone equals signs that aren't part of equations
  protectedContent = protectedContent.replace(
    /(?:(?:,|or|use)\s*)(\s*=\s*)(?:to|,|\s|$)/gi,
    (match, equals) => match.replace(equals, ' \\(=\\) '),
  );

  // Handle standalone math symbols and expressions
  protectedContent = protectedContent.replace(
    /\\(sqrt|sum|int|lim)\{([^}]*)\}/g,
    '\\(\\$1{$2}\\)',
  );

  // Handle Greek letters when standalone
  protectedContent = protectedContent.replace(
    /\\(alpha|beta|gamma|delta|epsilon|theta|lambda|mu|pi|sigma|phi|psi|omega)\b/g,
    '\\(\\$1\\)',
  );

  // Restore protected sections
  protectedSections.forEach((section, index) => {
    protectedContent = protectedContent.replace(
      `__PROTECTED_MATH_${index}__`,
      section,
    );
  });

  return protectedContent;
}

/**
 * Renders math equations in HTML content using KaTeX
 * @param htmlContent - The HTML content that may contain math equations
 * @returns The HTML content with math equations rendered
 */
export function renderMathInHtml(htmlContent: string): string {
  if (!htmlContent) return '';

  // First preprocess to wrap undelimited math expressions
  console.log('[KaTeX Debug] Original content:', htmlContent);
  const preprocessedContent = preprocessMathContent(htmlContent);
  console.log('[KaTeX Debug] After preprocessing:', preprocessedContent);

  // Create a temporary element to process the content
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = preprocessedContent;

  // Check if katex is available and use our local auto-render function
  if (typeof window !== 'undefined' && window.katex) {
    try {
      renderMathInElement(tempDiv, {
        delimiters: [
          { left: '$$', right: '$$', display: true },
          { left: '\\(', right: '\\)', display: false },
          { left: '\\[', right: '\\]', display: true },
        ],
        ignoredTags: [
          'script',
          'noscript',
          'style',
          'textarea',
          'pre',
          'code',
          'option',
        ],
        ignoredClasses: ['no-katex'],
        errorCallback: (msg: string, err: any) => {
          console.warn('KaTeX auto-render error:', msg, err);
        },
      });
    } catch (error) {
      console.warn('Error processing math equations:', error);
    }
  }

  return tempDiv.innerHTML;
}
